import { useTranslation } from "react-i18next";
import { EVENT_STATUSES } from "@/types/(company)/events.interface";
import { CalendarCheck2, CalendarClock, CalendarX, CalendarMinus, ClockFading, Calendar } from "lucide-react";

const buildEventStatus = (t: (key: string) => string) => ({
  [EVENT_STATUSES.COMPLETED]: {
    icon: CalendarCheck2,
    iconBg: "bg-green-400/20",
    iconColor: "text-green-500",
    badgeBg: "bg-green-100",
    badgeText: "text-green-600",
    label: t("ui.completed"),
  },
  [EVENT_STATUSES.UPCOMING]: {
    icon: CalendarClock,
    iconBg: "bg-orange-400/20",
    iconColor: "text-orange-500",
    badgeBg: "bg-orange-100",
    badgeText: "text-orange-600",
    label: t("ui.upcoming"),
  },
  [EVENT_STATUSES.CANCELLED]: {
    icon: CalendarX,
    iconBg: "bg-yellow-400/20",
    iconColor: "text-yellow-500",
    badgeBg: "bg-yellow-100",
    badgeText: "text-yellow-600",
    label: t("ui.cancelled"),
  },
  [EVENT_STATUSES.ARCHIVED]: {
    icon: CalendarMinus,
    iconBg: "bg-gray-300/40",
    iconColor: "text-gray-400",
    badgeBg: "bg-border",
    badgeText: "text-gray-500",
    label: t("ui.archived"),
  },
  [EVENT_STATUSES.EXPIRED]: {
    icon: ClockFading,
    iconBg: "bg-gray-300/40",
    iconColor: "text-gray-400",
    badgeBg: "bg-border",
    badgeText: "text-gray-500",
    label: t("ui.expired"),
  },
  default: {
    icon: Calendar,
    iconBg: "bg-violet-300/40",
    iconColor: "text-violet-400",
    badgeBg: "bg-violet-200",
    badgeText: "text-violet-500",
    label: t("ui.unknown_status"),
  },
});

interface EventCardProps {
  title: string;
  startDate: string;
  endDate: string;
  status: EVENT_STATUSES;
}

export const EventCard = ({ title, startDate, endDate, status }: EventCardProps) => {
  const { t } = useTranslation("my-space");
  const eventStatus = buildEventStatus(t)[status] || buildEventStatus(t).default;
  const Icon = eventStatus.icon;

  return (
    <div className="flex flex-wrap items-start gap-2 sm:gap-3 p-2 sm:p-3 border rounded-xs bg-white">
      <div
        className={`flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xs ${eventStatus.iconBg}`}
      >
        <Icon size={20} className={eventStatus.iconColor} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-xs sm:text-sm font-medium truncate">{title}</p>
        <p className="mt-1 text-[10px] sm:text-xs text-black/50 truncate">
          {startDate} - {endDate}
        </p>
      </div>
      {/* Status badge */}
      <span
        className={`shrink-0 self-start rounded-xs px-2 sm:px-3 py-1 text-[10px] font-medium ${eventStatus.badgeBg} ${eventStatus.badgeText}`}
      >
        {eventStatus.label}
      </span>
    </div>
  );
};
